/**
 * MSW Overrides: Multiple Subscriptions scenario
 *
 * Appends mocked subscriptions to the real logged-in subscriber so the
 * subscription list can be tested end-to-end in local development: three of
 * the same PRO plan (stacked, two of them active as numbered tabs) and an UNLIMITED one.
 *
 * Enable by setting ENABLE_MSW=true and NUXT_PUBLIC_MSW_OVERRIDES_MULTIPLE_SUBSCRIPTIONS=true
 * in your .env, then restart the dev server.
 */

import type { HttpHandler, PathParams } from 'msw';
import { http, HttpResponse, bypass } from 'msw';
import { BFF_ENDPOINTS, createMockSubscription, toMSWPattern } from '@cvo/domain';
import type { Quota, SubscriberResponse, SubscriptionResponse } from '@cvo/domain';

const PRO_PLAN: SubscriptionResponse['plan'] = {
  code: 'VEL-PRO-RT2-MONTHLY-PLAN',
  product: { code: 'PRO-RT2-MONTHLY', family: 'PRO', tripType: 'RT', group: 'DUO' },
};

const PRO_QUOTA: Quota = {
  limit: 2,
  used: 1,
  usage: 'MONTHLY',
  type: 'PER_JOURNEY',
  unlimited: false,
  periodEnd: '2026-12-31T23:59:59Z',
};

const MOCK_SUBSCRIPTIONS: SubscriptionResponse[] = [
  createMockSubscription({
    id: 'msw-subscription-pro',
    plan: PRO_PLAN,
    quota: PRO_QUOTA,
  }),
  createMockSubscription({
    id: 'msw-subscription-pro-2',
    plan: PRO_PLAN,
    quota: { ...PRO_QUOTA, used: 0 },
  }),
  createMockSubscription({
    id: 'msw-subscription-pro-3',
    status: 'SUSPENDED',
    displayStatus: 'SUSPENDED',
    plan: PRO_PLAN,
    quota: { ...PRO_QUOTA, used: 2 },
  }),
  createMockSubscription({
    id: 'msw-subscription-unlimited',
    plan: {
      code: 'VEL-UNLIMITED-YEARLY-PLAN',
      product: { code: 'UNLIMITED-YEARLY', family: 'UNLIMITED', tripType: 'ALL', group: 'SOLO' },
    },
    quota: {
      limit: 0,
      used: 4,
      usage: 'ANNUAL',
      type: 'PER_JOURNEY',
      unlimited: true,
      periodEnd: '2027-06-30T23:59:59Z',
    },
  }),
];

export function createMultipleSubscriptionsOverrides(baseUrl: string): HttpHandler[] {
  const sanitizedBaseUrl = baseUrl.replace(/\/$/, '');

  return [
    http.get(
      `${sanitizedBaseUrl}/${toMSWPattern(BFF_ENDPOINTS.USER_INFO)}`,
      async ({ request }) => {
        const originalResponse = await fetch(bypass(request));
        if (!originalResponse.ok) return originalResponse;

        const subscriber: SubscriberResponse = await originalResponse.json();

        return HttpResponse.json({
          ...subscriber,
          // @ts-expect-error Type expects auto convertion to camelCase, which is not the case in this handler.
          subscription_ids: [...subscriber.subscription_ids, ...MOCK_SUBSCRIPTIONS.map(({ id }) => id)],
        });
      },
    ),

    http.get(
      `${sanitizedBaseUrl}/${toMSWPattern(BFF_ENDPOINTS.SUBSCRIPTION)}`,
      ({ params }: { params: PathParams }) => {
        const subscription = MOCK_SUBSCRIPTIONS.find(({ id }) => id === params.subscriptionId);

        // Real subscriptions fall through to the default handlers or the BFF.
        return subscription ? HttpResponse.json(subscription) : undefined;
      },
    ),
  ];
}
